import React, { useMemo } from 'react'
import { subscribe } from 'react-contextual'
import { Heading, InfoList } from '../../../../../views/components'
import EditIcon from '../../../../../views/components/atoms/Svgs/Edit'
import ContactsProvider from '../../../state/ContactsProvider'
import styles from './ContactInfo.module.scss'

type AddressType = {
  addressLine1: string
  addressLine2: string
  city: string
  province: string
  postalCode: string
  country: string
}

type ContactInfoType = {
  name: string
  lastModified: string
  email: string
  contactNumber: string
  bank: React.ReactNode
  accountNumber: string
  idNumber: string
  passportNumber: string
  taxNumber: string
  companyRegistration: string
  vatNumber: string
  partyName: string
  companyName: string
  address: AddressType
}
interface ContactInfoProps {
  pathname: string
  redirect: (path: string) => void
  contactInfo: ContactInfoType
}

const ContactInfo = ({ pathname, redirect, contactInfo }: ContactInfoProps) => {
  const infoListItem = useMemo(
    () => [
      {
        label: 'Email',
        value: contactInfo.email,
        mailTo: true,
        copyToClipboard: true,
      },
      {
        label: 'Number',
        value: contactInfo.contactNumber,
      },
      {
        label: 'ID',
        value: contactInfo.idNumber,
        isVisible: Boolean(contactInfo.idNumber),
      },
      {
        label: 'Passport',
        value: contactInfo.passportNumber,
        isVisible: Boolean(contactInfo.passportNumber),
      },
      {
        label: 'Tax',
        value: contactInfo.taxNumber,
        isVisible: Boolean(contactInfo.taxNumber),
      },
      {
        label: 'Reg',
        value: contactInfo.companyRegistration,
        isVisible: Boolean(contactInfo.companyRegistration),
      },
      {
        label: 'VAT',
        value: contactInfo.vatNumber,
        isVisible: Boolean(contactInfo.vatNumber),
      },
    ],
    [contactInfo],
  )

  const bankAccountItems = useMemo(
    () => [
      {
        label: 'Bank',
        value: contactInfo.bank,
      },
      {
        label: 'Account',
        value: contactInfo.accountNumber,
      },
    ],
    [contactInfo],
  )

  const addressLine2 = contactInfo.address.addressLine2 ? `, ${contactInfo.address.addressLine2}` : ''
  const province = contactInfo.address.province ? `, ${contactInfo.address.province}` : ''

  return (
    <div className={styles.root}>
      <header>
        <button className={styles.edit} onClick={() => redirect(`${pathname}/edit`)}>
          <EditIcon />
        </button>
        <Heading as="h2" className={styles.name}>
          {contactInfo.partyName}
        </Heading>
        {contactInfo.partyName !== contactInfo.companyName && (
          <Heading as="h3" className={styles['sub-title']}>
            {contactInfo.companyName}
          </Heading>
        )}
        <small className={styles.lastModified}>Last updated: {contactInfo.lastModified}</small>
      </header>
      <section>
        <Heading as="h3">Contact details</Heading>
        <InfoList items={infoListItem} />
      </section>

      {bankAccountItems.filter(item => !!item.value).length > 0 && (
        <section>
          <Heading as="h3">Bank account</Heading>
          <InfoList spacing="none" items={bankAccountItems} />
        </section>
      )}

      {contactInfo.address && (
        <section>
          <Heading as="h3">Address</Heading>
          <p>
            {contactInfo.address.addressLine1}
            {addressLine2}
            {contactInfo.address.addressLine1 || addressLine2 ? <br /> : ''}
            {contactInfo.address.city}
            {province}
            {contactInfo.address.city || province ? <br /> : ''}
            {contactInfo.address.postalCode}
            {contactInfo.address.postalCode ? <br /> : ''}
            {contactInfo.address.country}
          </p>
        </section>
      )}
    </div>
  )
}

export default subscribe(ContactsProvider, ({ location, redirect, currentContactInfo: contactInfo }: any) => ({
  pathname: location.pathname,
  redirect,
  contactInfo,
}))(ContactInfo)
